import { clsxm } from '@ui/utils/style.util';
import { ComponentProps, createElement, JSX, memo, ReactNode, useMemo } from 'react';
import { Control, FieldPath, FieldValues } from 'react-hook-form';
import FormErrorMessage, {
  FormErrorMessageProps,
} from '../form-error-message/form-error-message.molecule';

// #region Types
type BaseLabelProps<
  FieldType extends FieldValues,
  Type extends keyof JSX.IntrinsicElements = 'p',
> = {
  containerClassName?: string;
  formErrorControl?: Control<FieldType>;
  formErrorName?: FieldPath<FieldType>;
  formErrorProps?: FormErrorMessageProps<FieldType, Type>;
  label?: ReactNode;
  withFormErrorMessage?: boolean;
};

export type LabelProps<
  FieldType extends FieldValues,
  Type extends keyof JSX.IntrinsicElements = 'p',
> = ComponentProps<'label'> &
  BaseLabelProps<FieldType, Type> & { containerProps?: ComponentProps<'div'>; hasInput?: boolean };

export type GroupLabelProps<
  FieldType extends FieldValues,
  Type extends keyof JSX.IntrinsicElements = 'p',
> = ComponentProps<'legend'> &
  BaseLabelProps<FieldType, Type> & { containerProps?: ComponentProps<'fieldset'> };
// #endregion

// #region Sub Components
function BaseFormErrorMessageHelper<
  FieldType extends FieldValues,
  Type extends keyof JSX.IntrinsicElements = 'p',
>({
  className,
  formErrorControl,
  formErrorName,
  label,
  ...rest
}: Pick<BaseLabelProps<FieldType, Type>, 'formErrorControl' | 'formErrorName' | 'label'> &
  FormErrorMessageProps<FieldType, Type>) {
  return (
    <FormErrorMessage<FieldType, Type>
      {...({
        control: formErrorControl,
        name: formErrorName,
        ...rest,
        className: clsxm('!mt-0.5 !-mb-1.5', !label && '!mt-0', className),
      } as FormErrorMessageProps<FieldType, Type>)}
    />
  );
}
BaseFormErrorMessageHelper.displayName = 'BaseFormErrorMessageHelper';

const FormErrorMessageHelper = memo(
  BaseFormErrorMessageHelper,
) as typeof BaseFormErrorMessageHelper;
// #endregion

function BaseLabel<FieldType extends FieldValues, Type extends keyof JSX.IntrinsicElements = 'p'>({
  children,
  containerClassName,
  containerProps,
  formErrorControl,
  formErrorName,
  formErrorProps,
  hasInput,
  htmlFor,
  label,
  withFormErrorMessage,
  ...rest
}: LabelProps<FieldType, Type>) {
  // #region Content
  const labelElement = useMemo(
    () =>
      (!!label || (!hasInput && children)) &&
      createElement(htmlFor ? 'label' : 'p', { ...rest, htmlFor }, label ?? children),
    [children, htmlFor, hasInput, label, rest],
  );
  // #endregion

  if (!label && !hasInput) return labelElement;

  return (
    <div
      {...containerProps}
      className={clsxm('flex flex-col space-y-2', containerClassName, containerProps?.className)}
    >
      {labelElement}
      {withFormErrorMessage && (
        <FormErrorMessageHelper<FieldType, Type>
          formErrorControl={formErrorControl}
          formErrorName={formErrorName}
          label={label}
          {...(formErrorProps as FormErrorMessageProps<FieldType, Type>)}
        />
      )}
      {children}
    </div>
  );
}

function GroupLabel<FieldType extends FieldValues, Type extends keyof JSX.IntrinsicElements = 'p'>({
  children,
  containerClassName,
  containerProps,
  formErrorControl,
  formErrorName,
  formErrorProps,
  label,
  withFormErrorMessage,
  ...rest
}: GroupLabelProps<FieldType, Type>) {
  return (
    <fieldset
      {...containerProps}
      className={clsxm('flex flex-col space-y-2', containerClassName, containerProps?.className)}
    >
      {label && <legend {...rest}>{label}</legend>}
      {withFormErrorMessage && (
        <FormErrorMessageHelper<FieldType, Type>
          formErrorControl={formErrorControl}
          formErrorName={formErrorName}
          label={label}
          {...(formErrorProps as FormErrorMessageProps<FieldType, Type>)}
        />
      )}
      {children}
    </fieldset>
  );
}

const Label = Object.assign(memo(BaseLabel) as typeof BaseLabel, {
  FormErrorMessage: FormErrorMessageHelper,
  Group: memo(GroupLabel) as typeof GroupLabel,
});

export default Label;
